import { StepAnswers, CareerRecommendation, CareerField } from '../types';
import { careerPaths } from '../data/careerPaths';

interface UserPreferenceScores {
  technical: number;
  analytical: number;
  creative: number;
  innovative: number;
  leadership: number;
  helping: number;
  scientific: number;
  detail_oriented: number;
  strategic: number;
  artistic: number;
  exploratory: number;
  social: number;
  service_oriented: number;
  articulate: number;
  ethical: number;
  adaptable: number;
}

// Initialize preference scores to 0
function initializePreferences(): UserPreferenceScores {
  return {
    technical: 0,
    analytical: 0,
    creative: 0,
    innovative: 0,
    leadership: 0,
    helping: 0,
    scientific: 0,
    detail_oriented: 0,
    strategic: 0,
    artistic: 0,
    exploratory: 0,
    social: 0,
    service_oriented: 0,
    articulate: 0,
    ethical: 0,
    adaptable: 0
  };
}

// Analyze answers using the traits and weights from options
function analyzeAnswers(answers: StepAnswers[]): UserPreferenceScores {
  const preferences = initializePreferences();

  answers.forEach(stepAnswer => {
    stepAnswer.answers.forEach(answer => {
      // Get the selected option's traits
      const traits = answer.answer.traits;
      if (traits) {
        // Add the weighted trait scores to preferences
        Object.entries(traits).forEach(([trait, weight]) => {
          preferences[trait as keyof UserPreferenceScores] += weight;
        });
      }
    });
  });

  return preferences;
}

// Calculate match score between career and user preferences
function calculateMatchScore(career: CareerField, preferences: UserPreferenceScores): number {
  let totalScore = 0;
  let maxPossibleScore = career.traits.length * 3; // Maximum possible score per trait

  // Map career traits to preference scores
  const traitMap: { [key: string]: keyof UserPreferenceScores } = {
    technical: 'technical',
    analytical: 'analytical',
    creative: 'creative',
    innovative: 'innovative',
    leadership: 'leadership',
    'problem-solving': 'analytical',
    collaborative: 'social',
    helping: 'helping',
    scientific: 'scientific',
    'detail-oriented': 'detail_oriented',
    strategic: 'strategic',
    artistic: 'artistic',
    exploratory: 'exploratory',
    'service-oriented': 'service_oriented',
    articulate: 'articulate',
    ethical: 'ethical',
    adaptable: 'adaptable'
  };

  // Calculate score based on traits
  career.traits.forEach(trait => {
    const preferenceKey = traitMap[trait];
    if (preferenceKey) {
      const preferenceScore = preferences[preferenceKey];
      totalScore += preferenceScore;
    }
  });

  // Normalize score to percentage (0-100)
  return Math.round((totalScore / maxPossibleScore) * 100);
}

// Generate reasons for match based on traits and scores
function generateMatchReasons(
  career: CareerField,
  preferences: UserPreferenceScores,
  score: number
): string[] {
  const reasons: string[] = [];
  const highTraitThreshold = 2;

  // Map traits to more descriptive reasons
  const traitReasons: { [key: string]: string } = {
    technical: "strong technical aptitude",
    analytical: "analytical thinking skills",
    creative: "creative abilities",
    innovative: "innovative mindset",
    leadership: "leadership qualities",
    helping: "desire to help others",
    scientific: "scientific approach",
    detail_oriented: "attention to detail",
    strategic: "strategic thinking",
    artistic: "artistic abilities",
    exploratory: "exploratory nature",
    social: "collaborative skills",
    service_oriented: "service-oriented mindset",
    articulate: "communication skills",
    ethical: "strong ethical foundation",
    adaptable: "adaptability"
  };

  // Add trait-based reasons
  career.traits.forEach(trait => {
    const preferenceKey = trait.replace('-', '_') as keyof UserPreferenceScores;
    if (preferences[preferenceKey] >= highTraitThreshold) {
      reasons.push(
        `Your ${traitReasons[trait] || trait} aligns well with the requirements of ${career.title}.`
      );
    }
  });

  // Add score-based general reason
  if (score >= 80) {
    reasons.push(
      `Your profile shows exceptional alignment with ${career.title}, particularly in key areas required for success in this field.`
    );
  } else if (score >= 60) {
    reasons.push(
      `You demonstrate strong potential for ${career.title}, with several matching qualities that could lead to success.`
    );
  } else if (score >= 40) {
    reasons.push(
      `You show some promising attributes for ${career.title}, though you might want to develop additional skills in this area.`
    );
  }

  return reasons;
}

// Main function to calculate career recommendations
export function calculateCareerRecommendations(answers: StepAnswers[]): CareerRecommendation[] {
  // Analyze answers to get preference scores
  const preferences = analyzeAnswers(answers);

  // Calculate match scores for each career path
  const recommendations = careerPaths.map(career => {
    const matchScore = calculateMatchScore(career, preferences);
    const reasons = generateMatchReasons(career, preferences, matchScore);

    return {
      title: career.title,
      description: career.description,
      matchPercentage: matchScore,
      reasonsForMatch: reasons,
      suggestedMajors: career.suggestedMajors,
      topColleges: career.topColleges,
      careerPaths: career.careerPaths
    };
  });

  // Sort by match percentage and filter out low matches
  return recommendations
    .sort((a, b) => b.matchPercentage - a.matchPercentage)
    .filter(rec => rec.matchPercentage >= 30);
}
